import type { DirectoryCategory } from "@/lib/categories";

/**
 * An admin-managed external affiliate link (e.g. an insurance provider's
 * referral URL). NOT a professional: no profile, no slug, no rating, no
 * packages. Ranks after every paid listing (premium, partner, founding) and
 * before every free one, on search, under articles and in the chat, and only
 * in the category the reader asked for. Spec: `affiliate-links-spec.md`.
 *
 * This is the PUBLIC projection: text is already resolved to the viewer's
 * locale, and `url` is never rendered as the link target. Cards link to
 * `affiliateHref(id, surface)`, the click-counting redirect, so the stored URL
 * can change without a cache purge and every click is counted server-side.
 */
export interface AffiliateLink {
  id: string;
  name: string;
  /** The destination, used only to show the reader which site they will land on. */
  url: string;
  /** "What it's for", one line, e.g. "Health insurance for people living abroad". Also the matching text. */
  tagline: string;
  /** Two or three sentences for the search card. */
  blurb: string;
  /** Public logo URL (Supabase storage), or null for the monogram fallback. */
  logo: string | null;
  categories: DirectoryCategory[];
  /**
   * Where it applies. "anywhere" (an online or worldwide service) and
   * "vietnam" match every location filter alike; they differ only in the
   * label the reader sees. "provinces" matches only `provinceSlugs`.
   */
  coverage: AffiliateCoverage;
  /** Used only when `coverage` is "provinces". */
  provinceSlugs: string[];
}

/**
 * What an affiliate carries into the CHAT client: an explicit projection, like
 * `toChatCard` for professionals. The model never sees it; code attaches it.
 */
export type AffiliateCard = Pick<AffiliateLink, "id" | "name" | "url" | "tagline" | "logo">;

export function toAffiliateCard(a: AffiliateLink): AffiliateCard {
  return { id: a.id, name: a.name, url: a.url, tagline: a.tagline, logo: a.logo };
}

export const AFFILIATE_COVERAGES = ["anywhere", "vietnam", "provinces"] as const;
export type AffiliateCoverage = (typeof AFFILIATE_COVERAGES)[number];

/** Where a click came from. Mirrors `affiliate_click_days_surface_check`. */
export const AFFILIATE_SURFACES = ["search", "article", "chat"] as const;
export type AffiliateSurface = (typeof AFFILIATE_SURFACES)[number];

/**
 * Field limits. Mirror the CHECK constraints in the `affiliate_links`
 * migration; the database is the boundary, these only give the admin a
 * readable error before it.
 */
export const AFFILIATE_LIMITS = {
  name: 80,
  url: 2048,
  tagline: 90,
  blurb: 300,
  maxCategories: 3,
} as const;

/** Storage bucket for affiliate logos (migration `affiliate_links`). */
export const AFFILIATE_LOGO_BUCKET = "affiliate-logos";
/** == the bucket's `file_size_limit`. The client resize lands far below it. */
export const AFFILIATE_LOGO_MAX_BYTES = 1024 * 1024;
/**
 * Logos are 3:4 portrait, 600 x 800, the same shape and size as a professional's
 * photo (owner, 2026-09-15), because they fill the same photo wells edge to
 * edge. Stated in the admin form.
 */
export const AFFILIATE_LOGO_WIDTH = 600;
export const AFFILIATE_LOGO_HEIGHT = 800;
/** How far from 3:4 a logo may be (5%) before it is refused. */
export const AFFILIATE_LOGO_SHAPE_TOLERANCE = 0.05;

/** The text an affiliate's matching vector is computed from (spec §4): what the reader sees. */
export function affiliateMatchText(taglineEn: string, blurbEn: string): string {
  return `${taglineEn.trim()}. ${blurbEn.trim()}`;
}

/** Public URL of a stored logo path ("<affiliate id>/<file>"), or null. */
export function affiliateLogoUrl(path: string | null | undefined): string | null {
  const base = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!path || !base) return null;
  return `${base.replace(/\/$/, "")}/storage/v1/object/public/${AFFILIATE_LOGO_BUCKET}/${path}`;
}

/**
 * `sponsored` is what search engines ask for on paid links; `nofollow` keeps
 * older crawlers from following it; `noopener` because it opens a new tab.
 * `noreferrer` is left out on purpose: some programmes attribute on the
 * referrer as well as the tracking parameter.
 */
export const AFFILIATE_REL = "sponsored nofollow noopener";

export function affiliateHref(id: string, surface: AffiliateSurface): string {
  return `/go/${encodeURIComponent(id)}?s=${surface}`;
}

export function isAffiliateSurface(value: unknown): value is AffiliateSurface {
  return typeof value === "string" && (AFFILIATE_SURFACES as readonly string[]).includes(value);
}

/** "safetywing.com" from "https://www.safetywing.com/nomad-insurance?ref=x". */
export function affiliateDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/**
 * The same shape the `affiliate_links_url_check` constraint enforces: https,
 * a plain host (letters, digits, dots, hyphens) with an optional port, no
 * userinfo, no whitespace, 2,048 characters at most. `user@host` is refused
 * because it can make a link read as one site while going to another.
 */
const AFFILIATE_URL_RE = /^https:\/\/[A-Za-z0-9.-]+(:[0-9]{1,5})?([/?#]|$)/;

export function isAffiliateUrl(value: string): boolean {
  if (!value || value.length > AFFILIATE_LIMITS.url || /\s/.test(value)) return false;
  if (!AFFILIATE_URL_RE.test(value)) return false;
  try {
    const u = new URL(value);
    return u.protocol === "https:" && !u.username && !u.password && u.hostname.includes(".");
  } catch {
    return false;
  }
}

export type LogoSizeVerdict = "ok" | "too_small" | "wrong_shape";

/**
 * Whether a source logo of this natural size is accepted (D10): 3:4 portrait,
 * at least 600 x 800. Shape is judged before size: a 1200 x 300 wordmark is
 * refused for its shape, which is the fix the admin needs, not for its height.
 */
export function checkLogoDimensions(width: number, height: number): LogoSizeVerdict {
  if (!(width > 0 && height > 0)) return "too_small";
  const target = AFFILIATE_LOGO_WIDTH / AFFILIATE_LOGO_HEIGHT;
  if (Math.abs(width / height / target - 1) > AFFILIATE_LOGO_SHAPE_TOLERANCE) return "wrong_shape";
  if (width < AFFILIATE_LOGO_WIDTH || height < AFFILIATE_LOGO_HEIGHT) return "too_small";
  return "ok";
}
